import app from 'flarum/forum/app';
import Link from 'flarum/common/components/Link';
import LogInModal from 'flarum/forum/components/LogInModal';
import { utils } from '@glowingblue-orell-thalia-common';
import type { GoogleAnalyticsSend, GoogleAnalyticsSendFields } from '@glowingblue-orell-thalia-common/utils/Auth';
import Article from './models/Article';
import type RequestError from 'flarum/common/utils/RequestError';

/**
 * The wishlist button's tracking payload. The shared `GoogleAnalyticsSendFields` names these
 * fields but leaves them all optional, and its index signature switches off excess-property
 * checking — so a typo would pass as just another arbitrary field. Requiring the four fields
 * this payload always carries turns that typo into a missing-property error instead.
 */
interface WishlistGaFields extends GoogleAnalyticsSendFields {
	hitType: string;
	eventCategory: string;
	eventAction: string;
	eventLabel: string;
}

function trackEvent(fields: WishlistGaFields): void {
	(window as { ga?: GoogleAnalyticsSend }).ga?.('send', fields);
}

function isRequestError(error: unknown): error is RequestError {
	return typeof error === 'object' && error !== null && typeof (error as { status?: unknown }).status === 'number';
}

export default class WishlistRepository {
	getWishlistUrl() {
		const base_url = app.forum.attribute('orellthalia_oAuthBaseUrl');
		return `${base_url}/shop/home/kunde/?utm_source=bookcircle.orellfuessli.ch&utm_medium=community&utm_campaign=merkzettel`;
	}

	async handleClick(article: Article, component: { loading: boolean }) {
		trackEvent({
			hitType: 'event',
			eventCategory: 'merkzettelButton',
			eventAction: 'click',
			eventLabel: 'Click on "Zum Merkzettel hinzufügen"',
		});

		if (!app.session.user) {
			app.alerts.show(app.translator.trans('glowingblue-orell-articles.forum.article.add_to_wishlist_login'));
			app.modal.show(LogInModal);
			return;
		}

		const add = async () => {
			component.loading = true;
			m.redraw();

			try {
				await app.request({
					url: `${app.forum.attribute('apiUrl')}/articles/wishlist`,
					method: 'POST',
					body: {
						data: {
							productId: article.productId(),
						},
					},
					errorHandler: () => {},
				});
			} catch (error) {
				component.loading = false;
				m.redraw();

				if (isRequestError(error) && error.status === 401) {
					// Auth.authenticatedCall will handle this
					throw error;
				} else {
					app.alerts.show(app.translator.trans('glowingblue-orell-articles.forum.article.add_to_wishlist_error'));
				}

				return;
			}

			component.loading = false;
			m.redraw();

			const alertId = app.alerts.show(
				{
					type: 'success',
					controls: [
						<Link href={this.getWishlistUrl()} external target="_blank">
							{app.translator.trans('glowingblue-orell-articles.forum.article.show_wishlist_label')}
						</Link>,
					],
				},
				app.translator.trans('glowingblue-orell-articles.forum.article.add_to_wishlist_success')
			);

			trackEvent({
				hitType: 'event',
				eventCategory: 'merkzettelButton',
				eventAction: 'success',
				eventLabel: 'Successfully added article to wishlist',
			});

			setTimeout(() => app.alerts.dismiss(alertId), 30000);
		};

		await utils.Auth.authenticatedCall(add, app.translator.trans('glowingblue-orell-articles.forum.article.add_to_wishlist_connect'), {
			hitType: 'event',
			eventCategory: 'merkzettelButton',
			eventAction: 'linkOFAccount',
			eventLabel: 'Successful linking of OF Account while clicking on "Zum Merkzettel hinzufügen"',
		});
	}
}
